import { Navigate, Outlet } from 'react-router-dom';
import type { ReactElement, ReactNode } from 'react';
import { ROUTES } from '@/app/config/constants';
import type { FeatureAction } from '@/core/security';
import { useFeatureAccess } from '../hooks/useFeatureAccess';

interface RequireFeatureProps {
  /** The `os_feature` name the route leads to, exactly as `/users/me` reports it. */
  feature: string;
  /** The action the route needs. Reaching a screen is VIEW, the grant that shows its menu; a route that only writes says so. */
  action?: FeatureAction;
  /** Wrap an element directly, or omit for a layout route and let the children render through `Outlet`. */
  children?: ReactNode;
}

/**
 * A route open to whoever the RBAC matrix says may reach the feature behind it.
 *
 * **Why this exists.** The sidebar decides what to show from the role's grants, and the master screens
 * decide their buttons from the same grants — but the routes were guarded by the role SLUG
 * (`requiredRoles={[ROLES.ADMIN]}`), a different source with only three values in it. Granting an
 * Initiator every permission on a master therefore put the entry in their sidebar and still answered
 * "You do not have access to that page" when they clicked it: the two layers were reading different
 * questions. This makes the route ask what the menu and the screen already ask.
 *
 * It takes a feature NAME rather than a path, so the check is the same wherever a feature is reached
 * from, and adding a master or a role changes data rather than code.
 *
 * Policy — deliberately the same as `useFeatureAccess`, so a route can never disagree with the menu that
 * led to it: **unknown grants deny nothing.** Before the profile resolves, and for a platform that does
 * not report grants at all, `can()` answers true and the route opens. That is the safe direction here
 * for two reasons: the platform independently refuses anything the role may not do, and a guard that
 * denied while the answer was still loading would bounce a legitimate user off their own page on every
 * refresh. `ProtectedRoute` has already held the render until the session is hydrated.
 */
export function RequireFeature({
  feature,
  action = 'view',
  children,
}: RequireFeatureProps): ReactElement {
  const { can } = useFeatureAccess();

  if (!can(feature, action)) {
    return <Navigate to={ROUTES.UNAUTHORIZED} replace />;
  }

  return <>{children ?? <Outlet />}</>;
}
